import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { disposeChildren, disposeResizeObserver, setDisposable } from "@cervval.js/client-html5-core/src/components/disposable";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, setDisplay, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO, POSITION, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement, empty, ignoreResizeObserverLoopLimit, removeAllTooltips, setHTMLElementAttributes } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { destroyObject } from "@cervval.js/common/src/tools/cache.tools";
import { removeFirstFromArray } from "@cervval.js/common/src/tools/collection.array.tools";
import { addToArrayRecord } from "@cervval.js/common/src/tools/collection.map.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { getVOFValue, objectValues } from "@cervval.js/common/src/tools/object.tools";
import { HOURS_IN_ONE_DAY, SECONDS_IN_ONE_HOUR } from "@cervval.js/common/src/tools/time.tools";
import { DEFAULT_DATE_FORMAT, TsDate, UnixTimestamp, beginOf, beginOfNext, diffRound, endOf, format, getIntersectingDays, getTodayDate, toDate } from "@cervval.js/common/src/tools/timestamps.tools";
import { CjsDateEditor } from "../../../../editors/editorsWithPicker/dateEditor/dateEditor";
import { CjsTimeEditor } from "../../../../editors/editorsWithPicker/timeEditor/timeEditor";
import { CjsDateNavigation } from "../../../filters/dateNavigation/dateNavigation";
import { CjsAgendaSelector } from "../../agendaSelector/agendaSelector";
import { CjsDayAgendaColumnView, DayAgendaColumnViewData, DayAgendaColumnViewOptions } from "../dayAgendaColumnView/dayAgendaColumnView";
import { CjsDayAgendaCursor } from "../dayAgendaCursor/dayAgendaCursor";
import { CjsDayAgendaHeader, DayAgendaHeaderTickInfo } from "../dayAgendaHeader/dayAgendaHeader";
import { installPanMove, installZoom } from "../dayAgendaNavigation/dayAgendaNavigation.helper";
import { CjsDayAgendaTimescale, DayAgendaTimescaleContext, rebuildTicksForAgenda } from "../dayAgendaTimescale/dayAgendaTimescale";
import { ColItemsHandler as ColumnItemsHandler, ColumnUid, DEFAULT_DAY_AGENDA_ROW_HEIGHT, DayAgendaMode } from "./dayAgenda.types";
import { buildColContextItems } from "./dayAgendaComputation.helper";
import { ColumnContext, DayAgendaContext } from "./dayAgendaContext";
import { getItemsByColumnUid } from "./dayAgendaData.helper";
import { RowClickEvent as ColumnClickEvent, ColumnContainerMouseDownEvent, ColumnMouseUpEvent, DayAgendaInteraction, DayAgendaRefreshReason, ItemClickEvent, ItemCreationFinishEvent, ItemDragEvent, ItemDropEvent, ItemInteraction, ItemRemoveFinishEvent, ItemResizeDragEvent, ItemResizeFinishEvent, getInteractionRefreshReason } from "./dayAgendaEvents";
import { installItemsInteractions } from "./dayAgendaInteractions.helper";
import { DayAgendaModel } from "./dayAgendaModel";
import { DayAgendaOptions, defaultDayAgendaOptions } from "./dayAgendaOptions";
import { DayAgendaSelectionManager } from "./dayAgendaSelection.helper";
import { installHoverInteraction } from "./dayAgendaUi.helper";
import { I18nType, i18n } from "./i18n/i18n";

@CustomElement({
    name: "cjs-day-agenda",
    style: require("./dayAgenda.scss"),
    template: require("./dayAgenda.html"),
    elementCssClasses: [FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO],
    i18n
})
export class CjsDayAgenda<ItemObject, ColumnObject> extends WebComponent<I18nType> {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    //#region Options
    /**
     * Agenda options
     */
    @TypedWebComponentOptions(defaultDayAgendaOptions)
    public readonly options!: WebComponentOptions<DayAgendaOptions<ItemObject, ColumnObject>>;

    /** Is set to true on colsContainer.mouseEnter, false on mouseLeave */
    protected _mouseIsInsideColumnsContainer = false;

    /** Agenda context */
    protected _context: DayAgendaContext = {
        startTs: getTodayDate().beginOf("week").uts,
        finishTs: endOf(getTodayDate(), "week").uts,
        beginX: 0, endY: 0, width: 0, height: 0, beginY: 0, endX: 0,
        isEditable: false,
        daysDisplayed: []
    };

    /** Agenda previous context, updated at the end of refresh */
    protected _previousContext: DayAgendaContext = { ...this._context };

    /* -- Components -- */
    @TypedHtmlElements({
        ensureLoadedClasses: [
            CjsDateEditor,
            CjsTimeEditor,
            CjsDayAgendaCursor,
            CjsDayAgendaTimescale,
            CjsDayAgendaHeader,
            CjsDateNavigation
        ]
    })
    public readonly components!: {
        cursor: CjsDayAgendaCursor<ItemObject, ColumnObject>,
        agendaHeader: CjsDayAgendaHeader,
        columnsContainer: HTMLDivElement,
        agendaTimescale: CjsDayAgendaTimescale,
        dateNavigation: CjsDateNavigation
    };

    // #region events

    /** Events dispatched */

    /** Item interaction events */

    /** On item click */
    @Event(NoBubblesEventInit)
    public itemClickEvent!: EventHandler<ItemClickEvent<ItemObject>>;

    /** On column click */
    @Event(NoBubblesEventInit)
    public columnClickEvent!: EventHandler<ColumnClickEvent<ItemObject, ColumnObject>>;

    /** On column container mouse down */
    @Event(NoBubblesEventInit)
    public columnContainerMouseDownEvent!: EventHandler<ColumnContainerMouseDownEvent<ItemObject, ColumnObject>>;

    /** On column mouse up */
    @Event(NoBubblesEventInit)
    public columnMouseUpEvent!: EventHandler<ColumnMouseUpEvent<ItemObject, ColumnObject>>;

    /** On after item creation */
    @Event(NoBubblesEventInit)
    public afterItemCreateEvent!: EventHandler<ItemCreationFinishEvent<ItemObject>>;

    /** On after item removal */
    @Event(NoBubblesEventInit)
    public afterItemRemoveEvent!: EventHandler<ItemRemoveFinishEvent<ItemObject>>;

    /** On after item resize */
    @Event(NoBubblesEventInit)
    public afterItemResizeEvent!: EventHandler<ItemResizeFinishEvent<ItemObject>>;

    /** On item resize */
    @Event(NoBubblesEventInit)
    public itemResizeDragEvent!: EventHandler<ItemResizeDragEvent<ItemObject>>;

    /** On item drag (start of item move action begins) */
    @Event(NoBubblesEventInit)
    public itemDragEvent!: EventHandler<ItemDragEvent<ItemObject>>;

    /** On item currently dragging, called each time item is moved */
    @Event(NoBubblesEventInit)
    public itemOnDragEvent!: EventHandler<ItemDragEvent<ItemObject>>;

    /** On item drop (item move action is finished) */
    @Event(NoBubblesEventInit)
    public itemDropEvent!: EventHandler<ItemDropEvent<ItemObject>>;

    /** On item interaction finish */
    @Event(NoBubblesEventInit)
    public itemInteractionFinishEvent!: EventHandler<{ event: ItemInteraction, initiator?: string }>;

    /** On item selection reset */
    @Event(NoBubblesEventInit)
    public itemSelectionResetEvent!: EventHandler;

    /** Agenda navigation events */

    /** On pan move (start of pan move) */
    @Event(NoBubblesEventInit)
    public panMoveEvent!: EventHandler;

    /** On pan move finish */
    @Event(NoBubblesEventInit)
    public panMoveFinishEvent!: EventHandler;

    /** On agenda zoom (start of zoom) */
    @Event(NoBubblesEventInit)
    public zoomEvent!: EventHandler;

    /** On agenda zoom finish */
    @Event(NoBubblesEventInit)
    public zoomFinishEvent!: EventHandler;

    /** On agenda navigation change */
    @Event(NoBubblesEventInit)
    public timeNavigationEvent!: EventHandler;

    /**
     * Event trigger when an interaction has updated the agenda render
     * * For interactions with no debounce where there is only a FinishEvent, this RefreshEvent is not emitted
     * * RefreshEvent is always followed by a FinishEvent
     * Merge of "ON_NAVIGATION", "ON_ZOOM", "ON_PAN"
     */
    @Event(NoBubblesEventInit)
    public interactionRefreshEvent!: EventHandler<{ event: DayAgendaInteraction }>;

    /**
     * Event trigger after an interaction has updated the agenda render
     * Merge of "ON_NAVIGATION", "ON_ZOOM_FINISH", "ON_PAN_FINISH" and other helpers navigation events (ex: agendaCellEdit)
     */
    @Event(NoBubblesEventInit)
    public interactionFinishEvent!: EventHandler<{ event: DayAgendaInteraction, refreshCount: number }>;

    /** Before agenda refresh but after context init */
    @Event(NoBubblesEventInit)
    public startOfRefreshEvent!: EventHandler<{ context: DayAgendaContext }>;

    /** On agenda end of refresh */
    @Event(NoBubblesEventInit)
    public endOfRefreshEvent!: EventHandler;

    /** On agenda end of view refresh */
    @Event(NoBubblesEventInit)
    public endOfRefreshViewEvent!: EventHandler;

    /** On before column view refresh */
    @Event(NoBubblesEventInit)
    public beforeRowViewRefresh!: EventHandler<{ columnView: CjsDayAgendaColumnView<ItemObject, ColumnObject>, refreshReason: string | undefined }>;

    // #endregion events

    /** Agenda column views by uid cache */
    public columnViewsByUidCache: { [columnUid: ColumnUid]: CjsDayAgendaColumnView<ItemObject, ColumnObject> } = {};

    /** Agenda column views by uid cache */
    public columnViewsByDayCache: { [dayTs: UnixTimestamp]: CjsDayAgendaColumnView<ItemObject, ColumnObject>[] } = {};

    /** AgendaTimescale(s) */
    public agendaTimescales: CjsDayAgendaTimescale[] = [];

    //#region Data

    /** Store item handlers of columns */
    protected _columnItemsHandler?: ColumnItemsHandler<ItemObject, ColumnObject>;

    /** Selection manager of agenda */
    public selectionManager = new DayAgendaSelectionManager<ItemObject>({
        getItemUid: (item) => {
            if (!this._agendaModel) { throw new Error("Agenda model is not defined."); }
            return this._agendaModel?.getItemUid(item);
        }
    });
    //#endregion Data

    /** Agenda model */
    protected _agendaModel?: DayAgendaModel<ItemObject, ColumnObject>;

    /** Check if interactions are installed */
    protected _isInteractionsInstalled = false;

    /** @returns true if an item is currently dragging */
    public isDraggingItem = false;

    /** elements to dispose when calling dispose function */
    protected _elementsToDispose: {
        resizeObserver?: ResizeObserver
    } = {};

    protected _isDisposed = false;

    /** true if agenda cursor was enable once on this agenda, used to avoid useless "setDisplay(NONE)" on cursor */
    protected _isCursorEnableOnce = false;

    /** Selected used to select mode */
    public modeSelector: CjsAgendaSelector;

    constructor(options?: Partial<DayAgendaOptions<ItemObject, ColumnObject>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        setDisposable(this);
        this.modeSelector = this.components.dateNavigation.modeSelector!;
        this.options.setWithoutRefresh(options);
    }

    /**
     * init agenda
     */
    public override init(): void {
        if (this._isDisposed) {
            // element was disposed => don't build it
            return;
        }

        this._initListeners();
    }

    /**
     * Refresh display
     */
    public override refresh(reason?: string): void {
        if (this._isDisposed) {
            this._log?.error("Element is disposed");
            return;
        }

        const debugTrace = this.debug.traceStart("refresh", reason);

        // Hide mode selector if required
        toggleVisible(this.modeSelector, !Boolean(this.options.disableModeSelector));

        try {
            this._refreshTimescales();

            const colsContainerBoundingRect = this.components.columnsContainer.getBoundingClientRect();
            const width = Math.floor(colsContainerBoundingRect.width);
            const height = Math.floor(colsContainerBoundingRect.height);

            if (reason === DayAgendaRefreshReason.RESIZE) {
                if (this._context.width === width && this._context.height === height) {
                    if (debugTrace) { debugTrace.message.push("ignored"); }
                    this.debug.traceFinish(debugTrace);
                    // no need to refresh
                    return;
                }
            }
            this.refreshContext({ width, height });

            this.startOfRefreshEvent.emit({ context: this._context });

            this.refreshView(reason);
            this.refreshHeader(reason);
            this.refreshDateNavigation();

            // Update previous context
            // ! Keep this line at the end of refresh
            this._previousContext = { ...this._context };

            // Dispatch end of refresh event.
            this.endOfRefreshEvent.emit();

            // install items interactions
            if (!this._isInteractionsInstalled && this._agendaModel?.isEditable()) {
                installItemsInteractions<ItemObject, ColumnObject>(this, this._agendaModel, this._log);
                this._isInteractionsInstalled = true;
            }

            if (this.options.enableHover) {
                installHoverInteraction<ItemObject, ColumnObject>(this);
            }

            this.debug.traceFinish(debugTrace);
        } catch (err) {
            this.debug.traceError(debugTrace, err);
            this._log?.error(err);
        }
    }

    /**
     * Call this function to change the data displayed by the agenda
     * There is no need to call refresh after
     */
    public setData(p: {
        dayAgendaModel: DayAgendaModel<ItemObject, ColumnObject>,
        columns: ColumnObject[],
        items: ItemObject[]
    }): void {
        if (this._isDisposed) {
            this._log?.error("Agenda is disposed");
            return;
        }

        this.columnViewsByDayCache = {};
        this.columnViewsByUidCache = {};

        // Set model
        this._agendaModel = p.dayAgendaModel;

        const itemsByColumnUid: { [columnUid: ColumnUid]: ItemObject[] } = getItemsByColumnUid(p.items, this._agendaModel);

        this._columnItemsHandler = {
            getColumnItems: columnUid => itemsByColumnUid[columnUid] ?? [],
            addItemToColCache: (columnUid, item) => {
                itemsByColumnUid[columnUid] ??= [];
                itemsByColumnUid[columnUid].push(item);
            },
            removeItemFromColumnCache: (columnUid, item) => {
                // Remove the item by identifying it with the agenda item comparator
                removeFirstFromArray(itemsByColumnUid[columnUid], item, this.itemComparator.bind(this));
            }
        };

        // Create columnsViews from given columns
        for (const column of p.columns) {
            this._createAgendaColumnView(column);
        }

        // refresh
        this.refresh(DayAgendaRefreshReason.SET_DATA);
    }

    /**
     * @returns get Column Uid from model + handles buffer column
     */
    protected _getColumnUid(p: { day?: UnixTimestamp, column?: ColumnObject }): string {
        return this._agendaModel?.getColumnUid(p) ?? "AGENDA_MODEL_IS_NOT_DEFINED";
    }

    /**
     * @returns get Column Uid from model + handles buffer column
     */
    protected _getColumnUidWithDay(day: TsDate): string {
        return `__column_${format(day, DEFAULT_DATE_FORMAT)}`;
    }

    /**
     * @param columnUid
     * @param colData colData is optional but should be passed when possible to avoid lookup later
     * @returns the items on the column identified by columnUid
     */
    public getColumnItems(columnUid: string, colData?: ColumnObject): ItemObject[] {
        return this._columnItemsHandler?.getColumnItems(columnUid, colData) ?? [];
    }

    /** Find item with his uid from given list */
    public findItemByUid(items: ItemObject[] | undefined, uid: string): ItemObject | undefined {
        /** Find item by uid, this function allows you to handle item uids that can change  */
        if (this._agendaModel == null || items == null) { return undefined; }
        if (this._agendaModel.findItemByUid) {
            return this._agendaModel.findItemByUid(items, uid);
        }
        return items.find(item => this._agendaModel?.getItemUid(item) === uid);
    }

    /** 
     * Called when creating an item or changing its column 
     * @param columnUid
     * @param item item to add
     * @param columnData columnData is optional but should be passed when possible to avoid lookup later
     */
    public addItemToColCache(columnUid: string, item: ItemObject, columnData: ColumnObject | undefined): void {
        this._columnItemsHandler?.addItemToColCache(columnUid, item, columnData);
    }

    /** 
     * Called when deleting an item or changing its column 
     * @param columnUid
     * @param item item to remove
     * @param columnData columnData is optional but should be passed when possible to avoid lookup later
     */
    public removeItemFromColumnCache(columnUid: string, item: ItemObject, columnData: ColumnObject | undefined): void {
        this._columnItemsHandler?.removeItemFromColumnCache(columnUid, item, columnData);
    }


    /** getter for rowMinHeight options or default */
    public getRowHeight(): number {
        return getVOFValue(this.options.rowHeight) ?? DEFAULT_DAY_AGENDA_ROW_HEIGHT;
    }


    /** _mouseIsInsideColumnsContainer is set to true on colsContainer.mouseEnter, false on mouseLeave */
    protected _installMouseIsInsideColumnsContainerListeners(): void {
        this.components.columnsContainer.addEventListener("mouseenter", () => {
            this._mouseIsInsideColumnsContainer = true;
        });
        this.components.columnsContainer.addEventListener("mouseleave", () => {
            this._mouseIsInsideColumnsContainer = false;
        });
    }

    /**
     * @returns true if mouse is currently inside the columns container (may be useful to deactivate agenda related keyboard actions)
     */
    public isMouseInsideColumnsContainer(): boolean {
        return this._mouseIsInsideColumnsContainer;
    }

    /**
     * install events listeners
     */
    protected _initListeners(): void {
        if (!this.options.disableZoom) {
            installZoom(this, this._context);
        }

        if (!this.options.disablePanMove) {
            installPanMove(this, this._context);
        }

        // Auto refresh on interaction
        this.interactionRefreshEvent.on((e) => {
            if (!this.options.disableAutoRefreshOnInteractionRefreshEvents) {
                this.refresh(getInteractionRefreshReason(e.detail?.event, { isFinish: false }));
            }
        });

        this.interactionFinishEvent.on((e) => {
            if (!this.options.disableAutoRefreshOnInteractionFinishEvents) {
                if (e.detail.refreshCount === 0) {
                    // if there was already a refresh done, we don't refresh again
                    this.refresh(getInteractionRefreshReason(e.detail?.event, { isFinish: true }));
                }
            }
        });

        this._installMouseIsInsideColumnsContainerListeners();

        this.itemDragEvent.on(() => this.isDraggingItem = true);
        this.itemDropEvent.on(() => this.isDraggingItem = false);

        // On monthNavigation date has changed
        this.components.dateNavigation.selectedDateChangedEvent.on(() => {
            const interval = this.components.dateNavigation.getCurrentInterval();

            let startTs = interval.start;
            let finishTs = interval.finish;

            const dayDiff = diffRound(startTs, finishTs, "day");

            // If dayDiff of context is one week, focus on start/end of week
            if (dayDiff === 7) {
                startTs = beginOf(startTs, "week").uts;
                finishTs = endOf(startTs, "week").uts;
            }

            // Set interval of agenda
            this.setTsInterval(startTs, finishTs);
            // Refresh navigate
            this.refresh(DayAgendaRefreshReason.NAVIGATE);
        });

        // Listen to header item click, focus agenda on day clicked
        this.components.agendaHeader.itemClickEvent.on(e => {
            // TODO Add option on this behavior with a custom callback
            const dateClicked = e.detail.dayClicked;

            // New interval is day clicked
            const startTs = dateClicked;
            const finishTs = endOf(startTs, "day").uts;

            // Set selector on day mode
            this.modeSelector.options.setAndRefreshIfChanged({ mode: "day" });

            // Set interval of agenda
            this.setTsInterval(startTs, finishTs);

            // Refresh navigate
            this.refresh(DayAgendaRefreshReason.NAVIGATE);
        });

        // Resize observer
        let timeoutId: number | undefined;
        const resizeObserver = new ResizeObserver(entries => {
            ignoreResizeObserverLoopLimit(entries, () => {
                if (timeoutId) {
                    // refresh is already planned => skip
                    return;
                }
                // setTimeout 1 => allow browser to render and avoid flicker due to fastDom optimizations
                timeoutId = setTimeout(() => {
                    clearTimeout(timeoutId);
                    timeoutId = undefined;
                    this.refresh(DayAgendaRefreshReason.RESIZE);
                }, 1);
            });
        });
        resizeObserver.observe(this);

        // On selected mode changed, update mode
        this.modeSelector.selectedModeChangeEvent.on(e => this.setMode(e.detail as DayAgendaMode));
    }

    /** Refresh month navigation from context */
    public refreshDateNavigation(): void {
        const dateNavigation = this.components.dateNavigation;
        if (this.options.only) {
            dateNavigation.modeSelector?.options.setAndRefreshIfChanged({ modesDisabled: ["month"] });
        }
        dateNavigation.options.setWithoutRefresh({
            ...this.options.dateNavigationOptions,
            durationUnit: {
                unit: this.modeSelector.options.mode,
                unitCount: 1
            }
        });
        dateNavigation.setSelectedDate(this._context.startTs, { refresh: true, doNotDispatchEvent: true });
    }

    /**
     * Refresh AgendaHeader
     */
    public refreshHeader(refreshReason?: string): void {
        const headerColumnsMap = new Map<ColumnUid, DayAgendaHeaderTickInfo>();

        // Loop on all days displayed
        for (const day of this._context.daysDisplayed) {

            // Get col views of this day
            const colViews = this.columnViewsByDayCache[day.uts];
            if (!colViews) { continue; }

            // FIXME Remove error in console for colViews not defined
            if (colViews == null) { break; }

            for (const colView of colViews) {
                const columnUid = colView.columnContext?.uid;
                if (!columnUid) { continue; }

                // Add colView to headerMap
                headerColumnsMap.set(
                    String(columnUid),
                    {
                        columnUid: String(columnUid),
                        date: day.uts
                    }
                );
            }
        }

        // Set width of header
        this.components.agendaHeader.style.width = toPx(this.components.columnsContainer.clientWidth);

        // Refresh agendaHeader
        this.components.agendaHeader.refresh(refreshReason, { headerColumnsMap });
    }

    /**
     * Refresh AgendaView
     * * if !autoScrollUpDisabled and !smartTableWillBeRefreshedLater, this call may trigger a refresh of the smartTable if an auto scroll up is performed
     * * if displayAllRows, append all columns to the agenda and not only visible columns
     * * if dispatchEvent true or not defined, endOfRefreshViewEvent will be emitted
     */
    public refreshView(refreshReason?: string, options?: { dispatchEvent?: boolean }): void {
        // Empty content
        empty(this.components.columnsContainer);

        removeAllTooltips();// FIXME this is needed because tooltips linked to items stays after scrolling, pan, refresh

        // Render column views
        this._appendAgendaColumnViews(refreshReason);

        // add ticks
        const columnsTicksContainer = createHtmlElement("div", { cssClass: [W_100, POSITION.ABSOLUTE] });
        // "prepend" ticks in order to be able to interact on columns
        this.components.columnsContainer.prepend(columnsTicksContainer);
        rebuildTicksForAgenda(columnsTicksContainer, this._context.agendaTimescaleInfos ?? []);

        // Refresh cursor
        this._refreshCursor();

        if (options?.dispatchEvent !== false) {
            this.endOfRefreshViewEvent.emit();
        }
    }

    /**
     * Appends agenda column views
     * Rows may be built or retrieved from the colViewsByUid cache
     */
    // eslint-disable-next-line complexity
    protected _appendAgendaColumnViews(refreshReason?: string): void {

        if (!this._agendaModel) { return; }

        // Sort, filter and display cols from smartTable cols. The smartTable dictates the cols to display and their order.
        for (const day of this._context.daysDisplayed) {
            let colViews = this.columnViewsByDayCache[day.uts];
            if (!colViews) {
                colViews = this._createAgendaColumnViewWithDay(day);
            }

            for (const columnView of colViews) {
                // refresh colView
                this.beforeRowViewRefresh.emit({ columnView, refreshReason });

                this.components.columnsContainer.append(columnView);
            }
        }

        // Refresh colViews only after all views are append to DOM
        for (const colView of objectValues(this.columnViewsByUidCache)) {
            colView.refresh(refreshReason, this._context);
        }

        return;
    }

    /**
     * build a AgendaColumnView instance
     */
    protected _buildAgendaColumnView(columnContext: ColumnContext<ItemObject, ColumnObject>): CjsDayAgendaColumnView<ItemObject, ColumnObject> {
        const data: DayAgendaColumnViewData<ItemObject, ColumnObject> = {
            context: this._context,
            columnContext
        };
        const cvOptions: Partial<DayAgendaColumnViewOptions> = {};

        const columnView = this.options.defaultColOptions.columnViewFactory?.(data, cvOptions);
        if (columnView != null) {
            return columnView;
        }
        return new CjsDayAgendaColumnView(data, cvOptions);
    }

    /**
     * Create a AgendaColumnView and registers it in the columnViewsByUidCache record
     * * does NOT refresh the colView nor append it to the column container
     * Need to check this._agendaModel before call of this function
     * @param column 
     * @returns the column view
     */
    protected _createAgendaColumnView(column: ColumnObject): CjsDayAgendaColumnView<ItemObject, ColumnObject> {

        const columnUid = this._getColumnUid({ column });
        let columnView = this.columnViewsByUidCache[columnUid];
        const agendaModel = this.getAgendaModel();

        if (!columnView && agendaModel) {
            const day = toDate(agendaModel.getColumnDay(column));
            const startTs = day.beginOf("day").uts;
            const finishTs = beginOfNext(day, "day").uts;

            // column is not in the cache => build it
            const columnContext: ColumnContext<ItemObject, ColumnObject> = {
                column: column,
                beginX: 0, beginY: 0, endX: 0, endY: 0, width: 0, height: 0,
                dayTs: startTs,
                startTs, finishTs,
                itemContexts: [],
                uid: columnUid
            };
            const items: ItemObject[] = this.getColumnItems(columnUid, column);

            const itemContexts = buildColContextItems(this._context, columnContext, items, this._agendaModel!, this.options.defaultColOptions, this.selectionManager);
            columnContext.itemContexts = itemContexts;


            columnView = this._buildAgendaColumnView(columnContext);

            this.columnViewsByUidCache[columnUid] = columnView;

            if (columnContext.column) {
                addToArrayRecord(this.columnViewsByDayCache, day.uts, columnView);
            }
        }

        return columnView;
    }

    /** Set mode of agenda */
    public setMode(mode: DayAgendaMode): void {
        const context = this.getAgendaContext();

        // From month to week
        if (mode === "week") {

            // Focus on first week of month
            const startTs = beginOf(context.startTs, "week").uts;
            const finishTs = endOf(context.startTs, "week").uts;
            this.setTsInterval(startTs, finishTs);
        }

        // From week or month to day
        else if (mode === "day") {

            // Focus on day of startTs
            const startTs = beginOf(context.startTs, "day").uts;
            const finishTs = endOf(context.startTs, "day").uts;
            this.setTsInterval(startTs, finishTs);
        }

        else {
            return;
        }


        // Save mode inside agenda and selector
        this.modeSelector.options.setAndRefreshIfChanged({ mode });

        // Finally refresh agenda
        this.refresh();
    }


    /** Refresh dayAgenda context */
    public refreshContext(p: { width: number, height: number }): void {
        const { width, height } = p;

        this._context.width = width;
        this._context.endX = this._context.width + this._context.beginX;
        this._context.height = height;
        this._context.endY = this._context.height + this._context.beginY;
        this._context.isEditable = this._agendaModel?.isEditable() ?? false;
        this._context.daysDisplayed = getIntersectingDays(this._context.startTs, this._context.finishTs, { strict: true });

        // Set current context in html
        setHTMLElementAttributes(this, {
            startTs: String(this._context.startTs),
            finishTs: String(this._context.finishTs)
        });

    }

    /**
     * Create a AgendaColumnView and registers it in the columnViewsByUidCache record
     * * does NOT refresh the colView nor append it to the column container
     * Need to check this._agendaModel before call of this function
     * @param column 
     * @param options
     * @param options.overflowYAuto if true, the column is scrollable (and scrolling in it won't scroll the agenda)
     * @param options.forcedHeight force height of column (not valid for buffer col)
     * @param options.maxHeight max height of column (not valid for buffer col)
     * @param options.stickyRowResizeTrHeight if set, this value in pixels will be added to the column height
     * @returns the column view
     */
    protected _createAgendaColumnViewWithDay(day: TsDate): CjsDayAgendaColumnView<ItemObject, ColumnObject>[] {

        const columnViews = this.columnViewsByDayCache[day.uts];
        const model = this.getAgendaModel();
        if (!columnViews?.length && model) {
            const columnUid = model.getColumnUid({ day: day.uts });
            const startTs = day.beginOf("day").uts;
            const finishTs = day.beginOfNext("day").uts;

            // column is not in the cache => build it
            const columnContext: ColumnContext<ItemObject, ColumnObject> = {
                uid: columnUid,
                beginX: 0, beginY: 0, endX: 0, endY: 0, width: 0, height: 0,
                dayTs: day.uts,
                startTs,
                finishTs,
                itemContexts: [],
                column: { dayTs: day.uts, uid: columnUid } as ColumnObject
            };

            const columnView = this._buildAgendaColumnView(columnContext);
            this.columnViewsByUidCache[columnUid] = columnView;
            addToArrayRecord(this.columnViewsByDayCache, day.uts, columnView);
            return [columnView];
        }

        return columnViews;
    }

    /** Refresh timescales */
    protected _refreshTimescales(): void {

        const timescalesContext: DayAgendaTimescaleContext[] = Array(HOURS_IN_ONE_DAY).fill(0).map((_, i) => {
            return {
                startTime: i * SECONDS_IN_ONE_HOUR,
                finishTime: (i + 1) * SECONDS_IN_ONE_HOUR,
                uid: String(i),
                height: this.getRowHeight()
            };
        });

        this.components.agendaTimescale.refresh(undefined, timescalesContext);
        this._context.agendaTimescaleInfos = this.components.agendaTimescale.getAgendaTimescaleInfos();
    }

    /** Init agenda cursor */
    protected _initCursor(): void {
        this.components.cursor.install(this);
    }

    /** Refresh display of cursor, based on "enableCursor" option */
    protected _refreshCursor(): void {
        if (this.options.enableCursor && !this._isCursorEnableOnce) {
            this._isCursorEnableOnce = true;
            this._initCursor();
        }
        if (!this._isCursorEnableOnce) { return; }

        // Continue refresh only if option is enable
        if (!this.options.enableCursor) {
            setDisplay(this.components.cursor, Display.NONE);
            return;
        }
    }

    /**
     * Set the agenda's context's start and finish timestamp
     * @param startTs 
     * @param finishTs 
     */
    public setTsInterval(startTs: UnixTimestamp, finishTs: UnixTimestamp): void {
        this._context.startTs = toDate(startTs).beginOf("day").uts;

        const finishDate = toDate(finishTs);
        if (finishDate.secondOfDay === 0) {
            this._context.finishTs = finishTs;
        } else {// Set to midnight
            this._context.finishTs = finishDate.beginOfNext("day").uts;
        }
    }

    /** @returns the agenda model if defined */
    public getAgendaModel(): DayAgendaModel<ItemObject, ColumnObject> | undefined {
        return this._agendaModel;
    }

    /** Get agenda context */
    public getAgendaContext(): DayAgendaContext {
        return this._context;
    }

    /** Return the X position of the column container */
    public getAgendaColumnsContainerX(): number {
        return this.components.columnsContainer.getBoundingClientRect().x;
    }
    /** Return the Y position of the column container */
    public getAgendaColumnsContainerY(): number {
        return this.components.columnsContainer.getBoundingClientRect().y;
    }

    /** Compare the items by their uid. Method used by the selection manager to compare items to be removed */
    public itemComparator(i1: ItemObject, i2: ItemObject): boolean {
        if (!this._agendaModel) {
            this._log?.error("Item comparator : The agenda Model hasn't been set.");
            return false;
        }
        return this._agendaModel.getItemUid(i1) === this._agendaModel.getItemUid(i2);
    }

    /**
     * returns the column from its uid
     * @deprecated this function is not deprecated but you should not use it in a loop (you should build a map instead)
     */
    public findColumnByUid(uid: ColumnUid): ColumnObject | undefined {
        // if column is in the cache, we get it directly
        const columnView = this.columnViewsByUidCache[uid];
        return columnView.columnContext?.column;
    }


    /**
     * Cleanup
     */
    public dispose(p?: { doNotDisposeChildren?: boolean }): void {
        if (this._isDisposed) {
            return;
        }
        this._isDisposed = true;

        this._columnItemsHandler = undefined;
        destroyObject(this._context);
        destroyObject(this._previousContext);
        this._agendaModel = undefined;

        disposeResizeObserver(this._elementsToDispose);

        if (!p?.doNotDisposeChildren) { disposeChildren(this); }
        this.lightDomRoot = undefined;
    }
}
